import React, { useEffect, useRef } from 'react'
import styled, { css } from 'styled-components'
import Brand from 'helpers/brand'
import { scrubText, scrubPhone } from 'helpers/inputCleaners'
import Textarea from 'components/Textarea'
import TextInput from 'components/TextInput'
import PhoneInput from 'components/PhoneInput'
import StatesDropdown from 'components/StatesDropdown'

import { screenSize } from 'styles/shared'

type errorProps = {
  firstName: string
  lastName: string
  zip: string
  email: string
  phone: string
}

type Props = {
  companyName: string
  firstName: string
  lastName: string
  address1: string | null
  address2: string | null
  city: string | null
  state: string | null
  zip: number | null
  email: string | null
  phone: string | null
  note: string
  errors: errorProps
  onChangeCallback: (updatedAttribute) => void
}

const CustomerFields = ({
  companyName,
  firstName,
  lastName,
  address1,
  address2,
  city,
  state,
  zip,
  email,
  phone,
  note,
  errors,
  onChangeCallback
}: Props) => {
  const companyNameInputRef = useRef<HTMLInputElement>(null)
  const firstNameInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (Brand.isCommercial()) {
      companyNameInputRef.current?.focus()
    } else {
      firstNameInputRef.current?.focus()
    }
  }, [])

  return (
    <>
      <h2>Customer Info</h2>

      {Brand.isCommercial() && (
        <S.TextInput
          label="Company Name"
          onChange={event => onChangeCallback({ companyName: scrubText(event.target.value) })}
          value={companyName}
          ref={companyNameInputRef}
        />
      )}

      <S.Row>
        <S.TextInput
            label="First Name"
            onChange={event => onChangeCallback({ firstName: scrubText(event.target.value) })}
            value={firstName}
            errorMessage={errors.firstName}
            ref={firstNameInputRef}
            isRequired
        />
        <S.TextInput
            label="Last Name"
            onChange={event => onChangeCallback({ lastName: scrubText(event.target.value) })}
            value={lastName}
            errorMessage={errors.lastName}
            isRequired
        />
      </S.Row>

      <S.Row>
        <S.TextInput
            label="Address 1"
            onChange={event => onChangeCallback({ address1: scrubText(event.target.value) })}
            value={address1 || ''}
            maxLength={255}
        />
        <S.TextInput
            label="Address 2"
            onChange={event => onChangeCallback({ address2: scrubText(event.target.value) })}
            value={address2 || ''}
            maxLength={255}
        />
      </S.Row>

      <S.Row>
        <S.TextInput
            label="City"
            onChange={event => onChangeCallback({ city: scrubText(event.target.value) })}
            value={city || ''}
        />
        <S.StatesDropdown
            onChange={event => onChangeCallback({ state: scrubText(event.target.value) })}
            value={state}
            short
        />
        <S.TextInput
            label="Zip"
            onChange={event => onChangeCallback({ zip: scrubText(event.target.value) })}
            value={zip || ''}
            maxLength={10}
            errorMessage={errors.zip}
            short
        />
      </S.Row>

      <S.Row>
        <S.PhoneInput
              label="Phone"
              onChange={event => onChangeCallback({ phone: scrubPhone(event.target.value) })}
              value={phone}
              errorMessage={errors.phone}
        />
        <S.TextInput
              label="Email"
              onChange={event => onChangeCallback({ email: scrubText(event.target.value) })}
              value={email || ''}
              errorMessage={errors.email}
        />
      </S.Row>

      <Textarea
        label="Notes"
        onChange={event => onChangeCallback({ note: event.target.value })}
        value={note}
      />

    </>
  )
}

export default CustomerFields

const textFieldStyle = (short) => {
  return css`
    display: inline-block;
    width: ${short ? '25%' : '50%'};

    @media ${screenSize.small} {
      width: 100%;
    }
  `
}

const S = {
  Row: styled.div`
    > :not(:last-child) {
      padding-right: 10px;
    }

    @media ${screenSize.small} {
      > :not(:last-child) {
        padding-right: 0px;
      }
    }
  `,
  TextInput: styled(TextInput).withConfig({
    shouldForwardProp: (prop) => !['short'].includes(prop)
  })<{short?: boolean}>`
    ${({ short }) => {
    return textFieldStyle(short)
  }}
  `,
  PhoneInput: styled(PhoneInput).withConfig({
    shouldForwardProp: (prop) => !['short'].includes(prop)
  })<{short?: boolean, isRequired?: boolean, className?: string }>`
    ${({ short }) => {
    return textFieldStyle(short)
  }}
  `,
  StatesDropdown: styled(StatesDropdown).withConfig({
    shouldForwardProp: (prop) => !['short'].includes(prop)
  })<{short?: boolean}>`
    ${({ short }) => {
    return textFieldStyle(short)
  }}
  `
}
